import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { 
  Plus, Briefcase, Clock, Play, CheckCircle, FileText, 
  CreditCard, Calendar, MapPin, AlertTriangle, Ban, RotateCcw, Settings,
  Filter, ArrowUpDown, ArrowUp, ArrowDown, CalendarDays, Car, Truck, Bike,
  MoreVertical, Eye, Printer, Edit, Trash2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import WorkflowDiagram from './WorkflowDiagram';
import ChangeStatusDialog from './ChangeStatusDialog';
import StatusBadge from './StatusBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobsManagement = ({ getAuthHeaders }) => {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [services, setServices] = useState([]);
  const [vehicles, setVehicles] = useState([]); // Store all vehicles
  const [drivers, setDrivers] = useState([]); // Store all drivers
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    company_id: '',
    customer_id: '',
    job_number: '',
    items: [{ service_product_id: '', quantity: 1, unit_price: 0 }],
    notes: ''
  });

  // Status management state
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [selectedJobForStatus, setSelectedJobForStatus] = useState(null);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [isJobDetailsDialogOpen, setIsJobDetailsDialogOpen] = useState(false);
  const [isEditingJobDetails, setIsEditingJobDetails] = useState(false);
  const [editedJobData, setEditedJobData] = useState({});
  
  // New reversible workflow states
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);
  const [jobToChangeStatus, setJobToChangeStatus] = useState(null);
  
  // Print state
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [jobToPrint, setJobToPrint] = useState(null);
  
  // Available statuses for jobs (full lifecycle)
  const jobStatuses = ['unscheduled', 'scheduled', 'in_progress', 'completed', 'invoiced', 'paid', 'cancelled'];
  
  // Scheduling state
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [selectedJobForScheduling, setSelectedJobForScheduling] = useState(null);
  const [scheduleData, setScheduleData] = useState({
    scheduled_start: '',
    scheduled_end: '',
    scheduled_location: '',
    address_1: '',
    address_2: '',
    address_3: '',
    city: '',
    county: '',
    postcode: '',
    location_type: 'mobile',
    flexible_timing: false,
    special_instructions: ''
  });

  // View mode state
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
    return new Date(today.setDate(diff));
  });
  const [draggedJob, setDraggedJob] = useState(null);

  // Filter and sorting state
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filteredJobs, setFilteredJobs] = useState([]);

  // Calendar helper functions
  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    setCurrentWeekStart(new Date(today.setDate(diff)));
  };

  const getJobsForDay = (date) => {
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    return jobs.filter(job => {
      if (!job.scheduled_start) return false;
      const jobDate = new Date(job.scheduled_start);
      return jobDate >= dayStart && jobDate <= dayEnd;
    });
  };

  const getFlexibleJobs = () => {
    // Only return jobs that are flexible AND have NO date set
    return jobs.filter(job => job.flexible_timing && !job.scheduled_start && job.status === 'scheduled');
  };

  const formatTime = (datetime) => {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const getDuration = (job) => {
    if (job.scheduled_start && job.scheduled_end) {
      const start = new Date(job.scheduled_start);
      const end = new Date(job.scheduled_end);
      return Math.round((end - start) / (1000 * 60)); // minutes
    }
    return 0;
  };

  // Vehicle helper functions
  const getVehicleIcon = (vehicleType) => {
    const type = vehicleType?.toLowerCase() || '';
    if (type.includes('van') || type.includes('truck') || type.includes('lorry')) {
      return Truck;
    } else if (type.includes('bike') || type.includes('motorcycle') || type.includes('motorbike') || type.includes('scooter')) {
      return Bike;
    }
    return Car; // Default to car icon
  };

  const getJobVehicles = (job) => {
    if (!job.vehicle_ids || job.vehicle_ids.length === 0) return [];
    const jobVehicles = job.vehicle_ids
      .map(vehicleId => vehicles.find(v => v.id === vehicleId))
      .filter(v => v); // Remove any undefined entries
    console.log(`Job ${job.job_number}: vehicle_ids=${job.vehicle_ids.length}, found=${jobVehicles.length}, total vehicles=${vehicles.length}`);
    return jobVehicles;
  };

  const getServicesForVehicle = (job, vehicleId) => {
    if (!job.items) return [];
    // Filter items that match this vehicle_id
    return job.items.filter(item => item.vehicle_id === vehicleId);
  };

  const getServicesWithoutVehicle = (job) => {
    if (!job.items) return [];
    // Get items that don't have a vehicle_id
    return job.items.filter(item => !item.vehicle_id);
  };

  useEffect(() => {
    fetchJobs();
    fetchCustomers();
    fetchContacts();
    fetchServices();
    fetchVehicles();
    fetchDrivers();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`, getAuthHeaders());
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, getAuthHeaders());
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, getAuthHeaders());
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts');
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`, getAuthHeaders());
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services');
    }
  };

  const fetchVehicles = async () => {
    try {
      // Fetch all customers' vehicles
      const customersResponse = await axios.get(`${API}/customers`, getAuthHeaders());
      const allVehicles = [];
      
      for (const customer of customersResponse.data) {
        try {
          const vehiclesResponse = await axios.get(`${API}/customers/${customer.id}/vehicles`, getAuthHeaders());
          allVehicles.push(...vehiclesResponse.data);
        } catch (error) {
          // Customer might not have vehicles, continue
          console.log(`No vehicles for customer ${customer.id}`);
        }
      }
      
      console.log('✅ Fetched vehicles:', allVehicles.length);
      setVehicles(allVehicles);
    } catch (error) {
      console.error('❌ Failed to fetch vehicles:', error);
      toast.error('Failed to load vehicle information');
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/drivers`, getAuthHeaders());
      setDrivers(response.data);
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
      // Don't show error toast since driver module might not be enabled
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/jobs`, formData, getAuthHeaders());
      toast.success('Job created successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    }
  };

  const resetForm = () => {
    setFormData({
      company_id: '',
      customer_id: '',
      job_number: '',
      items: [{ service_product_id: '', quantity: 1, unit_price: 0 }],
      notes: ''
    });
  };

  const addJobItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { service_product_id: '', quantity: 1, unit_price: 0 }]
    });
  };

  const updateJobItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'service_product_id') {
      const service = services.find(s => s.id === value);
      if (service) {
        newItems[index].unit_price = service.price;
      }
    }
    setFormData({ ...formData, items: newItems });
  };

  const getStatusBadge = (status, onClick = null) => {
    const statusConfig = {
      unscheduled: { color: 'bg-gray-100 text-gray-700', icon: Clock },
      scheduled: { color: 'bg-blue-100 text-blue-700', icon: Calendar },
      in_progress: { color: 'bg-yellow-100 text-yellow-700', icon: Play },
      completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      invoiced: { color: 'bg-purple-100 text-purple-700', icon: FileText },
      paid: { color: 'bg-emerald-100 text-emerald-700', icon: CreditCard },
      cancelled: { color: 'bg-red-100 text-red-700', icon: Ban }
    };

    const config = statusConfig[status] || statusConfig.unscheduled;
    const IconComponent = config.icon;

    const badgeContent = (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color} ${onClick ? 'cursor-pointer hover:opacity-80' : ''}`}>
        <IconComponent className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </div>
    );

    return onClick ? (
      <div onClick={onClick} className="inline-block">
        {badgeContent}
      </div>
    ) : badgeContent;
  };

  // Status management functions
  const openStatusDialog = async (job) => {
    try {
      setSelectedJobForStatus(job);
      
      // Fetch available statuses for this job
      const response = await axios.get(`${API}/jobs/${job.id}/available-statuses`, getAuthHeaders());
      setAvailableStatuses(response.data.available_transitions);
      
      setIsStatusDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load available statuses');
    }
  };

  const handleViewJobDetails = (job) => {
    setSelectedJobForStatus(job);
    setEditedJobData({
      scheduled_start: job.scheduled_start || '',
      scheduled_end: job.scheduled_end || '',
      scheduled_location: job.scheduled_location || '',
      address_1: job.address_1 || '',
      address_2: job.address_2 || '',
      address_3: job.address_3 || '',
      city: job.city || '',
      county: job.county || '',
      postcode: job.postcode || '',
      location_type: job.location_type || 'mobile',
      flexible_timing: job.flexible_timing || false,
      special_instructions: job.special_instructions || '',
      notes: job.notes || '',
      contact_id: job.contact_id || ''
    });
    setIsEditingJobDetails(false);
    setIsJobDetailsDialogOpen(true);
  };

  const handleEditJobDetails = () => {
    setIsEditingJobDetails(true);
  };

  const handleCancelEditJobDetails = () => {
    setIsEditingJobDetails(false);
    // Reset edited data to original
    if (selectedJobForStatus) {
      setEditedJobData({
        scheduled_start: selectedJobForStatus.scheduled_start || '',
        scheduled_end: selectedJobForStatus.scheduled_end || '',
        scheduled_location: selectedJobForStatus.scheduled_location || '',
        address_1: selectedJobForStatus.address_1 || '',
        address_2: selectedJobForStatus.address_2 || '',
        address_3: selectedJobForStatus.address_3 || '',
        city: selectedJobForStatus.city || '',
        county: selectedJobForStatus.county || '',
        postcode: selectedJobForStatus.postcode || '',
        location_type: selectedJobForStatus.location_type || 'mobile',
        flexible_timing: selectedJobForStatus.flexible_timing || false,
        special_instructions: selectedJobForStatus.special_instructions || '',
        notes: selectedJobForStatus.notes || '',
        contact_id: selectedJobForStatus.contact_id || ''
      });
    }
  };

  const handleSaveJobDetails = async () => {
    if (!selectedJobForStatus) return;

    try {
      // Prepare schedule data - handle null/undefined dates
      const scheduleUpdateData = {
        scheduled_location: editedJobData.scheduled_location || '',
        address_1: editedJobData.address_1 || '',
        address_2: editedJobData.address_2 || '',
        address_3: editedJobData.address_3 || '',
        city: editedJobData.city || '',
        county: editedJobData.county || '',
        postcode: editedJobData.postcode || '',
        location_type: editedJobData.location_type || 'mobile',
        flexible_timing: editedJobData.flexible_timing || false,
        special_instructions: editedJobData.special_instructions || ''
      };

      // Only add dates if they exist
      if (editedJobData.scheduled_start) {
        scheduleUpdateData.scheduled_start = editedJobData.scheduled_start;
      }
      if (editedJobData.scheduled_end) {
        scheduleUpdateData.scheduled_end = editedJobData.scheduled_end;
      }

      // Update scheduling details
      await axios.put(
        `${API}/jobs/${selectedJobForStatus.id}/schedule`, 
        scheduleUpdateData, 
        getAuthHeaders()
      );

      // Update notes and contact
      const jobUpdateData = {
        notes: editedJobData.notes || '',
        contact_id: editedJobData.contact_id || null
      };

      await axios.patch(
        `${API}/jobs/${selectedJobForStatus.id}`, 
        jobUpdateData, 
        getAuthHeaders()
      );

      toast.success('Job details updated successfully');
      setIsEditingJobDetails(false);
      
      // Refresh jobs and close/reopen dialog to show updated data
      await fetchJobs();
      setIsJobDetailsDialogOpen(false);
    } catch (error) {
      console.error('Failed to update job details:', error);
      toast.error(error.response?.data?.detail || 'Failed to update job details');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, job) => {
    e.stopPropagation(); // Prevent triggering the click handler
    setDraggedJob(job);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragEnd = (e) => {
    setDraggedJob(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Allow drop
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetDate, targetHour) => {
    e.preventDefault();
    
    // Remove drag highlight
    e.currentTarget.classList.remove('bg-green-100', 'border-2', 'border-green-400', 'border-dashed');
    
    if (!draggedJob) return;

    try {
      // Calculate new scheduled_start based on drop target
      const newDate = new Date(targetDate);
      newDate.setHours(targetHour, 0, 0, 0);
      
      // Calculate new scheduled_end (keep same duration)
      const originalDuration = getDuration(draggedJob);
      const newEndDate = new Date(newDate);
      newEndDate.setMinutes(newEndDate.getMinutes() + originalDuration);

      // Update job via API
      const updateData = {
        scheduled_start: newDate.toISOString(),
        scheduled_end: newEndDate.toISOString(),
        scheduled_location: draggedJob.scheduled_location,
        address_1: draggedJob.address_1,
        address_2: draggedJob.address_2,
        address_3: draggedJob.address_3,
        city: draggedJob.city,
        county: draggedJob.county,
        postcode: draggedJob.postcode,
        location_type: draggedJob.location_type,
        flexible_timing: false, // No longer flexible if dragged to specific time
        special_instructions: draggedJob.special_instructions
      };

      await axios.put(`${API}/jobs/${draggedJob.id}/schedule`, updateData, getAuthHeaders());
      
      toast.success(`Job ${draggedJob.job_number} rescheduled to ${newDate.toLocaleString('en-GB')}`);
      
      // Refresh jobs
      fetchJobs();
    } catch (error) {
      console.error('Failed to reschedule job:', error);
      toast.error('Failed to reschedule job');
    }
    
    setDraggedJob(null);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedJobForStatus) return;
    
    try {
      const statusData = {
        status: newStatus,
        reason: statusChangeReason
      };
      
      await axios.put(`${API}/jobs/${selectedJobForStatus.id}/status`, statusData, getAuthHeaders());
      await fetchJobs();
      
      setIsStatusDialogOpen(false);
      setSelectedJobForStatus(null);
      setStatusChangeReason('');
      
      toast.success(`Job status updated to ${newStatus}`);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update job status';
      toast.error(errorMessage);
    }
  };

  // New reversible status change handler
  const handleChangeStatus = async (newStatus, reason) => {
    if (!jobToChangeStatus) return;
    
    try {
      const statusData = {
        status: newStatus,
        reason: reason || undefined
      };
      
      await axios.put(`${API}/jobs/${jobToChangeStatus.id}/status`, statusData, getAuthHeaders());
      await fetchJobs();
      
      setChangeStatusDialogOpen(false);
      setJobToChangeStatus(null);
      
      toast.success(`Job status updated to ${newStatus}`);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update job status';
      toast.error(errorMessage);
    }
  };

  const openChangeStatusDialog = (job) => {
    setJobToChangeStatus(job);
    setChangeStatusDialogOpen(true);
  };

  // Scheduling functions
  const openScheduleDialog = (job) => {
    setSelectedJobForScheduling(job);
    
    // Pre-fill existing schedule data if available
    setScheduleData({
      scheduled_start: job.scheduled_start ? new Date(job.scheduled_start).toISOString().slice(0, 16) : '',
      scheduled_end: job.scheduled_end ? new Date(job.scheduled_end).toISOString().slice(0, 16) : '',
      scheduled_location: job.scheduled_location || '',
      scheduled_address: job.scheduled_address || '',
      location_type: job.location_type || 'mobile',
      special_instructions: job.special_instructions || ''
    });
    
    setIsScheduleDialogOpen(true);
  };

  const handleScheduleJob = async () => {
    if (!selectedJobForScheduling) return;
    
    try {
      await axios.put(`${API}/jobs/${selectedJobForScheduling.id}/schedule`, scheduleData, getAuthHeaders());
      await fetchJobs();
      
      setIsScheduleDialogOpen(false);
      setSelectedJobForScheduling(null);
      
      toast.success('Job scheduled successfully');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to schedule job';
      toast.error(errorMessage);
    }
  };

  // Filter and sort functions
  const applyFiltersAndSort = useCallback(() => {
    let filtered = [...jobs];
    
    // Apply date filters
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    switch (dateFilter) {
      case 'today':
        filtered = filtered.filter(job => {
          if (!job.scheduled_start) return false;
          const jobDate = new Date(job.scheduled_start);
          return jobDate >= today && jobDate < tomorrow;
        });
        break;
      case 'tomorrow':
        filtered = filtered.filter(job => {
          if (!job.scheduled_start) return false;
          const jobDate = new Date(job.scheduled_start);
          const dayAfterTomorrow = new Date(tomorrow);
          dayAfterTomorrow.setDate(tomorrow.getDate() + 1);
          return jobDate >= tomorrow && jobDate < dayAfterTomorrow;
        });
        break;
      case 'week':
        filtered = filtered.filter(job => {
          if (!job.scheduled_start) return false;
          const jobDate = new Date(job.scheduled_start);
          return jobDate >= weekStart && jobDate <= weekEnd;
        });
        break;
      case 'month':
        filtered = filtered.filter(job => {
          if (!job.scheduled_start) return false;
          const jobDate = new Date(job.scheduled_start);
          return jobDate >= monthStart && jobDate <= monthEnd;
        });
        break;
      case 'custom':
        if (customDateRange.start && customDateRange.end) {
          const startDate = new Date(customDateRange.start);
          const endDate = new Date(customDateRange.end);
          endDate.setHours(23, 59, 59, 999);
          filtered = filtered.filter(job => {
            if (!job.scheduled_start) return false;
            const jobDate = new Date(job.scheduled_start);
            return jobDate >= startDate && jobDate <= endDate;
          });
        }
        break;
      case 'unscheduled':
        filtered = filtered.filter(job => job.status === 'unscheduled' || !job.scheduled_start);
        break;
      default:
        // 'all' - no filtering
        break;
    }

    // Apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        switch (sortField) {
          case 'job_number':
            aValue = a.job_number || '';
            bValue = b.job_number || '';
            break;
          case 'customer':
            aValue = customers.find(c => c.id === a.customer_id)?.name || '';
            bValue = customers.find(c => c.id === b.customer_id)?.name || '';
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          case 'scheduled':
            aValue = a.scheduled_start ? new Date(a.scheduled_start).getTime() : 0;
            bValue = b.scheduled_start ? new Date(b.scheduled_start).getTime() : 0;
            break;
          case 'location':
            aValue = a.scheduled_location || '';
            bValue = b.scheduled_location || '';
            break;
          case 'total':
            aValue = a.total || 0;
            bValue = b.total || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredJobs(filtered);
  }, [jobs, customers, dateFilter, customDateRange, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };

  // Generate HTML for printing - opens in new window
  const generatePrintHTML = (job) => {
    const customer = customers.find(c => c.id === job.customer_id);
    const contact = contacts.find(c => c.id === job.contact_id);
    const jobVehicles = getJobVehicles(job);
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Job Details - ${job.job_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; margin: 0; }
            h1 { font-size: 24px; margin-bottom: 20px; }
            h3 { font-size: 16px; font-weight: bold; margin-top: 20px; margin-bottom: 10px; }
            .section { margin-bottom: 20px; padding: 15px; border: 1px solid #e5e7eb; border-radius: 8px; }
            .bg-blue { background-color: #eff6ff; }
            .bg-purple { background-color: #faf5ff; }
            .bg-green { background-color: #f0fdf4; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
            .text-sm { font-size: 14px; }
            .font-medium { font-weight: 500; }
            .font-bold { font-weight: 700; }
            .text-gray { color: #6b7280; }
            .vehicle-item { background: white; padding: 12px; margin-bottom: 10px; border: 1px solid #e5e7eb; border-radius: 4px; }
            .service-item { background: #f9fafb; padding: 12px; margin-bottom: 8px; border-left: 4px solid #3b82f6; }
            .flex-between { display: flex; justify-content: space-between; }
            .border-top { border-top: 1px solid #e5e7eb; padding-top: 12px; margin-top: 12px; }
            
            /* Print and screen controls */
            .no-print { display: block; }
            @media print {
              .no-print { display: none; }
            }
            .close-btn {
              position: fixed;
              top: 10px;
              right: 10px;
              padding: 10px 20px;
              background: #ef4444;
              color: white;
              border: none;
              border-radius: 6px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 600;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .close-btn:hover {
              background: #dc2626;
            }
            .print-instructions {
              background: #fef3c7;
              border: 1px solid #fbbf24;
              padding: 15px;
              border-radius: 8px;
              margin-bottom: 20px;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <div class="no-print">
            <button class="close-btn" onclick="window.close()">✕ Close Window</button>
            <div class="print-instructions">
              <strong>📝 Instructions:</strong> The print dialog should open automatically. After printing or canceling, click the red "Close Window" button above, or this window may close automatically.
            </div>
          </div>
          
          <h1>Job Details - ${job.job_number}</h1>
          
          <div class="section bg-blue">
            <h3>Customer Information</h3>
            <div class="grid text-sm">
              <div>
                <span class="text-gray">Customer:</span>
                <div class="font-medium">${customer?.name || 'Unknown'}</div>
              </div>
              <div>
                <span class="text-gray">Contact:</span>
                <div class="font-medium">${contact ? `${contact.first_name} ${contact.last_name}` : 'N/A'}</div>
              </div>
            </div>
          </div>
          
          ${jobVehicles.length > 0 ? `
            <div class="section bg-purple">
              <h3>Vehicles for this Job</h3>
              ${jobVehicles.map(v => `
                <div class="vehicle-item">
                  <div class="font-bold">${v.registration_number}</div>
                  <div class="text-sm">${v.make} ${v.model} ${v.year ? `(${v.year})` : ''}</div>
                  ${v.color ? `<div class="text-sm text-gray">Color: ${v.color}</div>` : ''}
                </div>
              `).join('')}
            </div>
          ` : ''}
          
          <div class="section">
            <h3>Scheduling Details</h3>
            <div class="grid text-sm">
              <div>
                <span class="text-gray">Status:</span>
                <div class="font-medium" style="text-transform: capitalize;">${job.status.replace('_', ' ')}</div>
              </div>
              <div>
                <span class="text-gray">Date:</span>
                <div class="font-medium">${job.scheduled_start ? new Date(job.scheduled_start).toLocaleDateString('en-GB') : 'Not scheduled'}</div>
              </div>
              <div>
                <span class="text-gray">Start Time:</span>
                <div class="font-medium">${job.flexible_timing ? 'Time Flexible' : job.scheduled_start ? new Date(job.scheduled_start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'Not set'}</div>
              </div>
              <div>
                <span class="text-gray">End Time:</span>
                <div class="font-medium">${job.scheduled_end ? new Date(job.scheduled_end).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'N/A'}</div>
              </div>
              <div style="grid-column: span 2;">
                <span class="text-gray">Duration:</span>
                <div class="font-medium">${getDuration(job)} minutes</div>
              </div>
            </div>
          </div>
          
          <div class="section">
            <h3>Location Details</h3>
            <div class="text-sm">
              <div style="margin-bottom: 8px;">
                <span class="text-gray">Location Type:</span>
                <div class="font-medium" style="text-transform: capitalize;">${job.location_type}</div>
              </div>
              <div style="margin-bottom: 8px;">
                <span class="text-gray">Location Name:</span>
                <div class="font-medium">${job.scheduled_location || 'N/A'}</div>
              </div>
              <div class="border-top">
                <span class="text-gray font-bold">Address:</span>
                <div class="font-medium" style="margin-top: 8px;">
                  ${job.address_1 || job.city || job.postcode ? `
                    ${job.address_1 ? `<div>${job.address_1}</div>` : ''}
                    ${job.address_2 ? `<div>${job.address_2}</div>` : ''}
                    ${job.address_3 ? `<div>${job.address_3}</div>` : ''}
                    ${job.city ? `<div>${job.city}</div>` : ''}
                    ${job.county ? `<div>${job.county}</div>` : ''}
                    ${job.postcode ? `<div class="font-bold">${job.postcode}</div>` : ''}
                  ` : 'N/A'}
                </div>
              </div>
              <div class="border-top">
                <span class="text-gray">Special Instructions:</span>
                <div class="font-medium" style="margin-top: 4px;">${job.special_instructions || 'N/A'}</div>
              </div>
            </div>
          </div>
          
          ${job.items && job.items.length > 0 ? `
            <div class="section">
              <h3>Services / Items</h3>
              ${job.items.map(item => {
                const service = services.find(s => s.id === item.service_product_id);
                const itemVehicle = item.vehicle_id ? vehicles.find(v => v.id === item.vehicle_id) : null;
                return `
                  <div class="service-item">
                    <div class="flex-between">
                      <div style="flex: 1;">
                        <div class="font-medium text-sm">${service?.name || item.description || 'Service'}</div>
                        ${itemVehicle ? `
                          <div class="text-sm" style="color: #1d4ed8; margin: 4px 0;">
                            <span class="font-bold">${itemVehicle.registration_number}</span>
                            <span class="text-gray"> ${itemVehicle.make} ${itemVehicle.model}</span>
                          </div>
                        ` : ''}
                        <div class="text-sm text-gray">Qty: ${item.quantity} × £${item.unit_price?.toFixed(2)}</div>
                      </div>
                      <div class="font-bold" style="font-size: 18px; margin-left: 16px;">£${item.line_total?.toFixed(2)}</div>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          ` : ''}
          
          <div class="section bg-green">
            <h3>Financial Summary</h3>
            <div class="text-sm">
              <div class="flex-between" style="margin-bottom: 8px;">
                <span class="text-gray">Subtotal:</span>
                <span class="font-medium">£${job.subtotal?.toFixed(2) || '0.00'}</span>
              </div>
              <div class="flex-between" style="margin-bottom: 8px;">
                <span class="text-gray">Tax:</span>
                <span class="font-medium">£${job.tax?.toFixed(2) || '0.00'}</span>
              </div>
              <div class="flex-between font-bold border-top" style="font-size: 18px;">
                <span>Total:</span>
                <span>£${job.total?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>
          
          ${job.notes ? `
            <div class="section">
              <h3>Notes</h3>
              <p class="text-sm">${job.notes}</p>
            </div>
          ` : ''}
          
          <script>
            // Auto-close on afterprint (works in Chrome, Firefox, Edge)
            window.onafterprint = function() {
              window.close();
            };
            
            // Fallback: If user closes print dialog without printing, still allow manual close
            // The close button will always be available as backup
          </script>
        </body>
      </html>
    `;
  };

  // Apply filters when dependencies change
  useEffect(() => {
    applyFiltersAndSort();
  }, [applyFiltersAndSort]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>Jobs</h2>
          <p className="text-gray-500">Manage and track jobs</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-gradient-to-r from-green-600 to-teal-600" data-testid="create-job-button">
              <Plus className="w-4 h-4" />
              Create Job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Job</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="job_number">Job Number</Label>
                <Input
                  id="job_number"
                  value={formData.job_number}
                  onChange={(e) => setFormData({ ...formData, job_number: e.target.value })}
                  required
                  placeholder="JOB-001"
                  data-testid="job-number-input"
                />
              </div>
              <div>
                <Label htmlFor="customer">Customer</Label>
                <Select value={formData.customer_id} onValueChange={(value) => setFormData({ ...formData, customer_id: value })}>
                  <SelectTrigger data-testid="job-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Job Items</Label>
                {formData.items.map((item, index) => (
                  <div key={index} className="flex gap-2">
                    <Select 
                      value={item.service_product_id} 
                      onValueChange={(value) => updateJobItem(index, 'service_product_id', value)}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.id} value={service.id}>{service.name} (${service.price})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateJobItem(index, 'quantity', parseInt(e.target.value))}
                      className="w-20"
                    />
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addJobItem}>Add Item</Button>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="job-submit-button">Create Job</Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Workflow Diagram */}
      <WorkflowDiagram currentStage="job" />

      {/* Job Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        {['unscheduled', 'scheduled', 'in_progress', 'completed', 'invoiced', 'paid', 'cancelled'].map(status => {
          const count = jobs.filter(j => j.status === status).length;
          return (
            <Card key={status} className="p-4 text-center">
              <div className="flex flex-col items-center gap-2">
                {getStatusBadge(status)}
                <div className="text-2xl font-bold">{count}</div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Quick Filters */}
      {/* View Mode Toggle */}
      <div className="flex gap-2 justify-end">
        <Button
          variant={viewMode === 'calendar' ? 'default' : 'outline'}
          onClick={() => setViewMode('calendar')}
          className="gap-2"
        >
          <Calendar className="w-4 h-4" />
          Calendar View
        </Button>
        <Button
          variant={viewMode === 'list' ? 'default' : 'outline'}
          onClick={() => setViewMode('list')}
          className="gap-2"
        >
          <FileText className="w-4 h-4" />
          List View
        </Button>
      </div>

      <Card className="p-4 mb-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold">Quick Filters</h3>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button
              variant={dateFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('all')}
            >
              All Jobs
            </Button>
            <Button
              variant={dateFilter === 'today' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('today')}
            >
              <CalendarDays className="w-4 h-4 mr-1" />
              Today's Jobs
            </Button>
            <Button
              variant={dateFilter === 'tomorrow' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('tomorrow')}
            >
              Tomorrow's Jobs
            </Button>
            <Button
              variant={dateFilter === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('week')}
            >
              This Week
            </Button>
            <Button
              variant={dateFilter === 'month' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('month')}
            >
              This Month
            </Button>
            <Button
              variant={dateFilter === 'unscheduled' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('unscheduled')}
            >
              <Clock className="w-4 h-4 mr-1" />
              Unscheduled Jobs
            </Button>
          </div>

          {/* Custom Date Range */}
          <div className="flex items-center gap-4">
            <Button
              variant={dateFilter === 'custom' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('custom')}
            >
              Custom Range
            </Button>
            {dateFilter === 'custom' && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                  className="w-40"
                  placeholder="Start date"
                />
                <span className="text-gray-500">to</span>
                <Input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                  className="w-40"
                  placeholder="End date"
                />
              </div>
            )}
          </div>

          {/* Filter Results Summary */}
          {dateFilter !== 'all' && (
            <div className="text-sm text-gray-600 bg-blue-50 p-2 rounded">
              Showing {filteredJobs.length} of {jobs.length} jobs
              {dateFilter === 'custom' && customDateRange.start && customDateRange.end && 
                ` from ${formatDate(customDateRange.start)} to ${formatDate(customDateRange.end)}`
              }
            </div>
          )}
        </div>
      </Card>

      {/* Loading state */}
      {isLoading ? (
        <div className="text-center py-12">Loading...</div>
      ) : jobs.length === 0 ? (
        <Card className="p-12 text-center">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No jobs yet. Create your first job!</p>
        </Card>
      ) : filteredJobs.length === 0 ? (
        <Card className="p-12 text-center">
          <Filter className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No jobs found matching the current filter.</p>
          <Button 
            variant="outline" 
            onClick={() => setDateFilter('all')} 
            className="mt-4"
          >
            Clear Filters
          </Button>
        </Card>
      ) : (
        <>
          {/* Calendar View */}
          {viewMode === 'calendar' && (
            <div className="space-y-4">
              {/* Week Navigation */}
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <Button variant="outline" onClick={() => navigateWeek(-1)}>
                    ← Previous Week
                  </Button>
                  <div className="flex items-center gap-4">
                    <h3 className="text-lg font-semibold">
                      Week of {currentWeekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </h3>
                    <Button variant="outline" size="sm" onClick={goToToday}>
                      Today
                    </Button>
                  </div>
                  <Button variant="outline" onClick={() => navigateWeek(1)}>
                    Next Week →
                  </Button>
                </div>
              </Card>

              {/* Weekly Time Grid */}
              <Card className="overflow-x-auto">
                <div className="min-w-[1400px]">
                  {/* Header: Days */}
                  <div className="grid grid-cols-8 border-b bg-gray-50 sticky top-0 z-10">
                    <div className="p-2 text-center border-r font-semibold text-xs text-gray-600">
                      Time
                    </div>
                    {getWeekDays().map((day, index) => {
                      const isToday = new Date().toDateString() === day.toDateString();
                      return (
                        <div 
                          key={index} 
                          className={`p-3 text-center border-r last:border-r-0 ${isToday ? 'bg-blue-100' : ''}`}
                        >
                          <div className="font-bold text-sm">{day.toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                          <div className={`text-xl font-bold ${isToday ? 'text-blue-600' : ''}`}>{day.getDate()}</div>
                          <div className="text-xs text-gray-500">{day.toLocaleDateString('en-GB', { month: 'short' })}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Time Slots: 6 AM - 8 PM */}
                  {Array.from({ length: 15 }, (_, hourIndex) => hourIndex + 6).map((hour) => {
                    const hourStr = `${String(hour).padStart(2, '0')}:00`;
                    
                    return (
                      <div key={hour} className="grid grid-cols-8 border-b hover:bg-gray-50">
                        {/* Time Label */}
                        <div className="p-2 text-center border-r bg-gray-50 text-xs font-medium text-gray-600">
                          {hourStr}
                        </div>
                        
                        {/* Day Columns */}
                        {getWeekDays().map((day, dayIndex) => {
                          const dayJobs = getJobsForDay(day);
                          const isToday = new Date().toDateString() === day.toDateString();
                          
                          // Separate flexible timing jobs (have date but no specific time) from timed jobs
                          const flexibleDayJobs = dayJobs.filter(job => job.flexible_timing);
                          const timedJobs = dayJobs.filter(job => !job.flexible_timing);
                          
                          // Filter timed jobs that start in this hour
                          const hourJobs = timedJobs.filter(job => {
                            if (!job.scheduled_start) return false;
                            const jobDate = new Date(job.scheduled_start);
                            return jobDate.getHours() === hour;
                          });
                          
                          // Show flexible jobs only in the first hour (6 AM) for that day
                          const showFlexibleJobs = hour === 6 && flexibleDayJobs.length > 0;
                          
                          return (
                            <div 
                              key={dayIndex}
                              className={`min-h-[60px] p-1 border-r last:border-r-0 transition-colors ${
                                isToday ? 'bg-blue-50' : ''
                              }`}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, day, hour)}
                              onDragEnter={(e) => e.currentTarget.classList.add('bg-green-100', 'border-2', 'border-green-400', 'border-dashed')}
                              onDragLeave={(e) => e.currentTarget.classList.remove('bg-green-100', 'border-2', 'border-green-400', 'border-dashed')}
                            >
                              {/* Flexible timing jobs at top of day (only show in first hour) */}
                              {showFlexibleJobs && flexibleDayJobs.map((job) => {
                                const customer = customers.find(c => c.id === job.customer_id);
                                const contact = contacts.find(c => c.id === job.contact_id);
                                
                                return (
                                  <div
                                    key={job.id}
                                    draggable={true}
                                    onDragStart={(e) => handleDragStart(e, job)}
                                    onDragEnd={handleDragEnd}
                                    onClick={() => handleViewJobDetails(job)}
                                    className={`p-2 mb-1 rounded border-2 border-dashed cursor-move hover:shadow-lg transition-all text-xs relative bg-orange-50 border-orange-400 ${draggedJob?.id === job.id ? 'opacity-50' : ''}`}
                                    title="Drag to reschedule or click for details"
                                  >
                                    {/* Status Badge */}
                                    <div className="absolute top-1 right-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-600 text-white">
                                      TIME FLEXIBLE
                                    </div>

                                    {/* Time */}
                                    <div className="font-bold text-orange-700 mb-1 text-xs">
                                      ⏱ Time Not Set
                                    </div>
                                    
                                    {/* Customer */}
                                    <div className="font-semibold text-gray-900 truncate mb-1">
                                      {customer?.name || 'Unknown'}
                                    </div>
                                    
                                    {/* Job Number */}
                                    <div className="text-gray-600 mb-1">
                                      {job.job_number}
                                    </div>
                                    
                                    {/* Location */}
                                    <div className="flex items-center gap-1 text-gray-600 mb-1">
                                      <MapPin className="w-3 h-3" />
                                      <span className="capitalize">{job.location_type}</span>
                                      {job.scheduled_location && (
                                        <span className="truncate">: {job.scheduled_location}</span>
                                      )}
                                    </div>
                                    
                                    {/* Contact */}
                                    {contact && (
                                      <div className="text-gray-600 truncate">
                                        👤 {contact.first_name} {contact.last_name}
                                      </div>
                                    )}
                                    
                                    {/* Vehicles (if available) */}
                                    {(() => {
                                      const jobVehicles = getJobVehicles(job);
                                      if (jobVehicles.length > 0) {
                                        return (
                                          <div className="space-y-1.5 mb-2 border-t pt-1 mt-1">
                                            {jobVehicles.map((vehicle, idx) => {
                                              const VehicleIcon = getVehicleIcon(vehicle.vehicle_type);
                                              const vehicleServices = getServicesForVehicle(job, vehicle.id);
                                              return (
                                                <div key={idx} className="mb-1.5">
                                                  <div className="flex items-center gap-1.5 text-[10px] text-blue-700">
                                                    <VehicleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                                    <span className="font-bold">{vehicle.registration_number}</span>
                                                    <span className="text-gray-600 truncate">
                                                      {vehicle.make} {vehicle.model}
                                                    </span>
                                                  </div>
                                                  {/* Services for this vehicle */}
                                                  {vehicleServices.length > 0 && (
                                                    <div className="ml-5 mt-0.5 space-y-0.5">
                                                      {vehicleServices.map((service, sIdx) => (
                                                        <div key={sIdx} className="text-[9px] text-gray-600 truncate">
                                                          • {service.description}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                    
                                    {/* Services without vehicle */}
                                    {(() => {
                                      const generalServices = getServicesWithoutVehicle(job);
                                      if (generalServices.length > 0) {
                                        return (
                                          <div className="mb-2 border-t pt-1 mt-1">
                                            <div className="mb-1.5">
                                              <div className="flex items-center gap-1.5 text-[10px] text-blue-700">
                                                <Car className="w-3.5 h-3.5 flex-shrink-0" />
                                                <span className="font-bold">TBA</span>
                                              </div>
                                              {/* Services without vehicle assignment */}
                                              <div className="ml-5 mt-0.5 space-y-0.5">
                                                {generalServices.map((service, idx) => (
                                                  <div key={idx} className="text-[9px] text-gray-600 truncate">
                                                    • {service.description}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                    
                                    {/* Total */}
                                    <div className="font-semibold text-gray-900 mt-1">
                                      £{job.total?.toFixed(2) || '0.00'}
                                    </div>
                                  </div>
                                );
                              })}

                              {/* Regular timed jobs */}
                              {hourJobs.map((job) => {
                                const customer = customers.find(c => c.id === job.customer_id);
                                const contact = contacts.find(c => c.id === job.contact_id);
                                const duration = getDuration(job);
                                
                                return (
                                  <div
                                    key={job.id}
                                    draggable={true}
                                    onDragStart={(e) => handleDragStart(e, job)}
                                    onDragEnd={handleDragEnd}
                                    onClick={() => handleViewJobDetails(job)}
                                    className={`p-2 mb-1 rounded border-l-4 cursor-move hover:shadow-lg transition-all text-xs relative ${
                                      job.status === 'scheduled' ? 'bg-blue-100 border-blue-600 hover:bg-blue-200' :
                                      job.status === 'in_progress' ? 'bg-yellow-100 border-yellow-600 hover:bg-yellow-200' :
                                      job.status === 'completed' ? 'bg-green-100 border-green-600 hover:bg-green-200' :
                                      'bg-gray-100 border-gray-500 hover:bg-gray-200'
                                    } ${draggedJob?.id === job.id ? 'opacity-50' : ''}`}
                                    title="Drag to reschedule or click for details"
                                  >
                                    {/* Status Badge */}
                                    <div className={`absolute top-1 right-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                      job.status === 'scheduled' ? 'bg-blue-600 text-white' :
                                      job.status === 'in_progress' ? 'bg-yellow-600 text-white' :
                                      job.status === 'completed' ? 'bg-green-600 text-white' :
                                      job.status === 'unscheduled' ? 'bg-gray-600 text-white' :
                                      'bg-red-600 text-white'
                                    }`}>
                                      {job.status === 'in_progress' ? 'IN PROGRESS' : job.status.toUpperCase()}
                                    </div>

                                    {/* Time + Duration */}
                                    <div className="font-bold text-gray-800 mb-1">
                                      {formatTime(job.scheduled_start)}
                                      {duration > 0 && <span className="text-gray-600"> ({duration}m)</span>}
                                    </div>
                                    
                                    {/* Customer Name (always show) */}
                                    <div className="font-semibold text-gray-900 truncate mb-1">
                                      {customer?.name || 'Unknown'}
                                    </div>
                                    
                                    {/* Contact Name (if exists) */}
                                    {contact && (
                                      <div className="text-[11px] text-gray-700 truncate mb-1 flex items-center gap-1">
                                        <span>👤</span>
                                        <span>{contact.first_name} {contact.last_name}</span>
                                      </div>
                                    )}
                                    
                                    {/* Location Type + Postcode (for mobile) or Location Name */}
                                    <div className="text-gray-700 mb-1 flex items-start gap-1">
                                      <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                      <div className="flex-1 min-w-0">
                                        <div className="capitalize text-[11px]">
                                          {job.location_type === 'mobile' ? (
                                            <>
                                              <span className="font-medium">Mobile</span>
                                              {job.postcode && (
                                                <span className="font-bold ml-1">{job.postcode}</span>
                                              )}
                                              {job.scheduled_location && !job.postcode && (
                                                <span className="text-gray-600 truncate block">{job.scheduled_location}</span>
                                              )}
                                            </>
                                          ) : (
                                            <>
                                              <span className="font-medium">On-site</span>
                                              {job.scheduled_location && (
                                                <span className="text-gray-600 truncate block">{job.scheduled_location}</span>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Vehicles (if available) */}
                                    {(() => {
                                      const jobVehicles = getJobVehicles(job);
                                      if (jobVehicles.length > 0) {
                                        return (
                                          <div className="space-y-1.5 mb-2 border-t pt-1 mt-1">
                                            {jobVehicles.map((vehicle, idx) => {
                                              const VehicleIcon = getVehicleIcon(vehicle.vehicle_type);
                                              const vehicleServices = getServicesForVehicle(job, vehicle.id);
                                              return (
                                                <div key={idx} className="mb-1.5">
                                                  <div className="flex items-center gap-1.5 text-[10px] text-blue-700">
                                                    <VehicleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                                    <span className="font-bold">{vehicle.registration_number}</span>
                                                    <span className="text-gray-600 truncate">
                                                      {vehicle.make} {vehicle.model}
                                                    </span>
                                                  </div>
                                                  {/* Services for this vehicle */}
                                                  {vehicleServices.length > 0 && (
                                                    <div className="ml-5 mt-0.5 space-y-0.5">
                                                      {vehicleServices.map((service, sIdx) => (
                                                        <div key={sIdx} className="text-[9px] text-gray-600 truncate">
                                                          • {service.description}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                    
                                    {/* Services without vehicle */}
                                    {(() => {
                                      const generalServices = getServicesWithoutVehicle(job);
                                      if (generalServices.length > 0) {
                                        return (
                                          <div className="mb-2 border-t pt-1 mt-1">
                                            <div className="mb-1.5">
                                              <div className="flex items-center gap-1.5 text-[10px] text-blue-700">
                                                <Car className="w-3.5 h-3.5 flex-shrink-0" />
                                                <span className="font-bold">TBA</span>
                                              </div>
                                              {/* Services without vehicle assignment */}
                                              <div className="ml-5 mt-0.5 space-y-0.5">
                                                {generalServices.map((service, idx) => (
                                                  <div key={idx} className="text-[9px] text-gray-600 truncate">
                                                    • {service.description}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                    
                                    {/* Job Number (smaller) */}
                                    <div className="text-[10px] text-gray-500">
                                      {job.job_number}
                                    </div>
                                    
                                    {/* Total */}
                                    <div className="font-semibold text-gray-900 mt-1">
                                      £{job.total?.toFixed(2) || '0.00'}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Flexible Timing Jobs WITHOUT Date */}
              {getFlexibleJobs().length > 0 && (
                <Card className="p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-orange-600" />
                    Unscheduled Flexible Jobs (No Date Set)
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">These jobs need a date assigned. Drag them to the calendar to schedule.</p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {getFlexibleJobs().map((job) => {
                      const customer = customers.find(c => c.id === job.customer_id);
                      const contact = contacts.find(c => c.id === job.contact_id);
                      const jobDate = job.scheduled_start ? new Date(job.scheduled_start).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : 'No date set';
                      
                      return (
                        <div
                          key={job.id}
                          onClick={() => handleViewJobDetails(job)}
                          className="p-3 border-2 rounded-lg bg-orange-50 border-orange-400 hover:shadow-lg transition-all cursor-pointer"
                        >
                          <div className="font-semibold text-sm mb-1">{customer?.name || 'Unknown'}</div>
                          <div className="text-xs text-gray-700 mb-1">{job.job_number}</div>
                          <div className="text-xs text-gray-700 mb-2">📅 {jobDate}</div>
                          {contact && (
                            <div className="text-xs text-gray-700 mb-1">👤 {contact.first_name} {contact.last_name}</div>
                          )}
                          <div className="text-xs text-gray-700 mb-1 capitalize flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {job.location_type}
                            {job.scheduled_location && `: ${job.scheduled_location}`}
                          </div>
                          <div className="text-sm font-semibold text-gray-900 mt-2">
                            £{job.total?.toFixed(2) || '0.00'}
                          </div>
                          <div className="text-xs font-semibold text-orange-700 mt-2">
                            ⏱ Time flexible
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('job_number')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Job # {getSortIcon('job_number')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('customer')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Customer {getSortIcon('customer')}
                  </Button>
                </TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Vehicles</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('status')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Status {getSortIcon('status')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('scheduled')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Scheduled {getSortIcon('scheduled')}
                  </Button>
                </TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('location')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Location {getSortIcon('location')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('total')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Total {getSortIcon('total')}
                  </Button>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredJobs.map((job) => {
                const contact = contacts.find(c => c.id === job.contact_id);
                const contactName = contact ? `${contact.first_name} ${contact.last_name}` : null;
                
                // Calculate duration in minutes if both start and end times exist
                let durationMinutes = null;
                if (job.scheduled_start && job.scheduled_end) {
                  const start = new Date(job.scheduled_start);
                  const end = new Date(job.scheduled_end);
                  durationMinutes = Math.round((end - start) / 60000);
                }
                
                return (
                <TableRow 
                  key={job.id} 
                  data-testid={`job-row-${job.id}`}
                  onClick={() => handleViewJobDetails(job)}
                  className={`cursor-pointer hover:bg-gray-50 transition-colors ${job.flexible_timing ? 'bg-orange-50' : ''}`}
                >
                  <TableCell>
                    <div className="font-medium" data-testid={`job-number-${job.id}`}>
                      {job.job_number}
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <div className="text-sm">
                      {customers.find(c => c.id === job.customer_id)?.name || 'Unknown Customer'}
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    {contactName ? (
                      <div className="text-sm">{contactName}</div>
                    ) : (
                      <span className="text-gray-400 text-sm">No contact</span>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    {(() => {
                      const jobVehicles = getJobVehicles(job);
                      const generalServices = getServicesWithoutVehicle(job);
                      
                      if (jobVehicles.length === 0 && generalServices.length === 0) {
                        return <span className="text-gray-400 text-sm">-</span>;
                      }
                      
                      return (
                        <div className="space-y-2">
                          {/* Vehicles with services */}
                          {jobVehicles.map((vehicle, idx) => {
                            const VehicleIcon = getVehicleIcon(vehicle.vehicle_type);
                            const vehicleServices = getServicesForVehicle(job, vehicle.id);
                            return (
                              <div key={idx} className="border-l-2 border-blue-400 pl-2 py-1">
                                <div className="flex items-center gap-1.5 text-xs">
                                  <VehicleIcon className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                  <span className="font-bold text-blue-700">{vehicle.registration_number}</span>
                                </div>
                                <div className="text-[11px] text-gray-600 ml-5">
                                  {vehicle.make} {vehicle.model}
                                </div>
                                {/* Services for this vehicle */}
                                {vehicleServices.length > 0 && (
                                  <div className="ml-5 mt-1 space-y-0.5">
                                    {vehicleServices.map((service, sIdx) => (
                                      <div key={sIdx} className="text-[10px] text-gray-500">
                                        • {service.description}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          
                          {/* Services without vehicle */}
                          {generalServices.length > 0 && (
                            <div className="border-l-2 border-blue-400 pl-2 py-1">
                              <div className="flex items-center gap-1.5 text-xs">
                                <Car className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                <span className="font-bold text-blue-700">TBA</span>
                              </div>
                              <div className="ml-5 mt-1 space-y-0.5">
                                {generalServices.map((service, idx) => (
                                  <div key={idx} className="text-[10px] text-gray-500">
                                    • {service.description}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <StatusBadge 
                      status={job.status} 
                      onClick={() => openChangeStatusDialog(job)}
                    />
                  </TableCell>
                  
                  <TableCell>
                    {job.scheduled_start ? (
                      <div className="text-sm">
                        <div className="font-medium">
                          {formatDate(job.scheduled_start)}
                        </div>
                        {job.flexible_timing ? (
                          <div className="flex items-center gap-1 text-orange-600 font-semibold">
                            <Clock className="w-3 h-3" />
                            <span>Time Flexible</span>
                          </div>
                        ) : (
                          <div className="text-gray-500">
                            {new Date(job.scheduled_start).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">Not scheduled</span>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    {durationMinutes ? (
                      <div className="text-sm">
                        <Clock className="w-3 h-3 inline mr-1 text-gray-400" />
                        {durationMinutes} min
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    {job.scheduled_location || job.location_type ? (
                      <div className="text-sm">
                        {job.scheduled_location && (
                          <div className="font-medium">{job.scheduled_location}</div>
                        )}
                        <div className="text-gray-500 capitalize">{job.location_type || 'mobile'}</div>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">Not set</span>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    <div className="text-sm">
                      <div className="font-bold text-green-600" data-testid={`job-total-${job.id}`}>
                        £{job.total.toFixed(2)}
                      </div>
                      <div className="text-gray-500">
                        Subtotal: £{job.subtotal.toFixed(2)}
                      </div>
                    </div>
                  </TableCell>
                  
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      {/* Primary Actions - Always Visible */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewJobDetails(job);
                        }}
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setJobToPrint(null); // Clear first
                          setTimeout(() => {
                            setJobToPrint(job);
                            setIsPrintDialogOpen(true);
                          }, 50); // Small delay to ensure clean state
                        }}
                        title="Print Job"
                        className="text-purple-600"
                      >
                        <Printer className="w-4 h-4" />
                      </Button>

                      {/* Schedule Button - Show for unscheduled/scheduled jobs */}
                      {(job.status === 'unscheduled' || job.status === 'scheduled') && (
                        <Button
                          size="sm"
                          variant={job.status === 'unscheduled' ? 'default' : 'outline'}
                          onClick={(e) => {
                            e.stopPropagation();
                            openScheduleDialog(job);
                          }}
                          className="gap-1"
                          title={job.status === 'unscheduled' ? 'Schedule Job' : 'Reschedule Job'}
                        >
                          <Calendar className="w-4 h-4" />
                          {job.status === 'unscheduled' ? 'Schedule' : 'Reschedule'}
                        </Button>
                      )}

                      {/* Secondary Actions - Dropdown Menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            openChangeStatusDialog(job);
                          }}>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Change Status
                          </DropdownMenuItem>

                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Edit job
                            toast.info('Edit functionality coming soon');
                          }}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Job
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />
                          
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Are you sure you want to delete this job?')) {
                                // TODO: Delete job
                                toast.info('Delete functionality coming soon');
                              }
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Job
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
          )}
        </>
      )}

      {/* Status Change Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Job Status</DialogTitle>
            <DialogDescription>
              Update the status for Job {selectedJobForStatus?.job_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedJobForStatus && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Current Status:</span>
                  {getStatusBadge(selectedJobForStatus.status)}
                </div>
                <div className="text-xs text-gray-600">
                  Customer: {customers.find(c => c.id === selectedJobForStatus.customer_id)?.name || 'Unknown'}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="new-status">New Status</Label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {availableStatuses.map((statusOption) => (
                  <Button
                    key={statusOption.status}
                    variant="outline"
                    onClick={() => handleStatusChange(statusOption.status)}
                    className="justify-start h-auto p-3 text-left"
                  >
                    <div className="flex items-center gap-3 w-full">
                      <div className="flex-shrink-0">
                        {getStatusBadge(statusOption.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{statusOption.label}</div>
                        <div className="text-xs text-gray-500">{statusOption.description}</div>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="status-reason">Reason (Optional)</Label>
              <textarea
                id="status-reason"
                value={statusChangeReason}
                onChange={(e) => setStatusChangeReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
                rows="2"
                placeholder="Add a note about this status change..."
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsStatusDialogOpen(false);
                  setSelectedJobForStatus(null);
                  setStatusChangeReason('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Details Dialog */}
      <Dialog open={isJobDetailsDialogOpen} onOpenChange={setIsJobDetailsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Job Details - {selectedJobForStatus?.job_number}</DialogTitle>
            <DialogDescription>
              {isEditingJobDetails ? 'Edit job information below' : 'View and manage job details'}
            </DialogDescription>
          </DialogHeader>

          {selectedJobForStatus && (
            <div className="space-y-4">
              {/* Customer & Contact Info */}
              <Card className="p-4 bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => !isEditingJobDetails && handleEditJobDetails()}>
                <h3 className="font-semibold mb-2 flex items-center justify-between">
                  Customer Information
                  {!isEditingJobDetails && <span className="text-xs text-gray-500">Click to edit</span>}
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Customer:</span>
                    <div className="font-medium">{customers.find(c => c.id === selectedJobForStatus.customer_id)?.name || 'Unknown'}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Contact:</span>
                    {isEditingJobDetails ? (
                      <Select
                        value={editedJobData.contact_id}
                        onValueChange={(value) => setEditedJobData({ ...editedJobData, contact_id: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select contact" />
                        </SelectTrigger>
                        <SelectContent>
                          {contacts.filter(c => c.customer_id === selectedJobForStatus.customer_id).map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.first_name} {contact.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="font-medium">
                        {(() => {
                          const contact = contacts.find(c => c.id === selectedJobForStatus.contact_id);
                          return contact ? `${contact.first_name} ${contact.last_name}` : 'N/A';
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              {/* Vehicles Section */}
              {(() => {
                const jobVehicles = getJobVehicles(selectedJobForStatus);
                if (jobVehicles.length > 0) {
                  return (
                    <Card className="p-4 bg-purple-50">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Car className="w-5 h-5" />
                        Vehicles for this Job
                      </h3>
                      <div className="space-y-3">
                        {jobVehicles.map((vehicle, idx) => {
                          const VehicleIcon = getVehicleIcon(vehicle.vehicle_type);
                          return (
                            <div key={idx} className="p-3 bg-white rounded-lg border border-purple-200">
                              <div className="flex items-start gap-3">
                                <VehicleIcon className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                                <div className="flex-1">
                                  <div className="font-bold text-lg text-purple-900">{vehicle.registration_number}</div>
                                  <div className="text-sm text-gray-700 mt-1">
                                    <span className="font-semibold">{vehicle.make} {vehicle.model}</span>
                                    {vehicle.year && <span className="ml-2">({vehicle.year})</span>}
                                  </div>
                                  {vehicle.color && (
                                    <div className="text-sm text-gray-600 mt-1">
                                      Color: <span className="font-medium">{vehicle.color}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  );
                }
                return null;
              })()}

              {/* Scheduling Info */}
              <Card className="p-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => !isEditingJobDetails && handleEditJobDetails()}>
                <h3 className="font-semibold mb-3 flex items-center justify-between">
                  Scheduling Details
                  {!isEditingJobDetails && <span className="text-xs text-gray-500">Click to edit</span>}
                </h3>
                <div className="space-y-3">
                  {/* Flexible Timing Checkbox */}
                  {isEditingJobDetails && (
                    <div className="flex items-center space-x-2 p-2 bg-orange-50 rounded">
                      <input
                        type="checkbox"
                        id="edit_flexible"
                        checked={editedJobData.flexible_timing}
                        onChange={(e) => setEditedJobData({ ...editedJobData, flexible_timing: e.target.checked })}
                        className="w-4 h-4 rounded"
                      />
                      <Label htmlFor="edit_flexible" className="cursor-pointer">
                        Customer is flexible on time
                      </Label>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <div className="font-medium capitalize">{selectedJobForStatus.status}</div>
                    </div>
                    
                    <div>
                      <span className="text-gray-600">Date:</span>
                      {isEditingJobDetails ? (
                        <Input
                          type="date"
                          value={editedJobData.scheduled_start ? editedJobData.scheduled_start.split('T')[0] : ''}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            const oldTime = editedJobData.scheduled_start ? editedJobData.scheduled_start.split('T')[1] : '09:00:00';
                            const newStart = newDate ? `${newDate}T${oldTime}` : '';
                            
                            // Update end date to maintain duration
                            let newEnd = editedJobData.scheduled_end;
                            if (editedJobData.scheduled_start && editedJobData.scheduled_end && newDate) {
                              const oldStartDate = new Date(editedJobData.scheduled_start);
                              const oldEndDate = new Date(editedJobData.scheduled_end);
                              const duration = oldEndDate - oldStartDate; // duration in milliseconds
                              
                              const newStartDate = new Date(newStart);
                              const newEndDate = new Date(newStartDate.getTime() + duration);
                              newEnd = newEndDate.toISOString();
                            }
                            
                            setEditedJobData({ 
                              ...editedJobData, 
                              scheduled_start: newStart,
                              scheduled_end: newEnd
                            });
                          }}
                          className="mt-1"
                        />
                      ) : (
                        <div className="font-medium">
                          {selectedJobForStatus.scheduled_start ? 
                            new Date(selectedJobForStatus.scheduled_start).toLocaleDateString('en-GB') : 
                            'Not scheduled'}
                        </div>
                      )}
                    </div>

                    <div>
                      <span className="text-gray-600">Start Time:</span>
                      {isEditingJobDetails ? (
                        <Input
                          type="time"
                          value={editedJobData.scheduled_start && editedJobData.scheduled_start.includes('T') ? editedJobData.scheduled_start.split('T')[1]?.substring(0, 5) : ''}
                          onChange={(e) => {
                            const date = editedJobData.scheduled_start ? editedJobData.scheduled_start.split('T')[0] : new Date().toISOString().split('T')[0];
                            const newStart = e.target.value ? `${date}T${e.target.value}:00` : '';
                            
                            // Update end time to maintain duration
                            let newEnd = editedJobData.scheduled_end;
                            if (editedJobData.scheduled_start && editedJobData.scheduled_end && e.target.value) {
                              const oldStartDate = new Date(editedJobData.scheduled_start);
                              const oldEndDate = new Date(editedJobData.scheduled_end);
                              const duration = oldEndDate - oldStartDate; // duration in milliseconds
                              
                              const newStartDate = new Date(newStart);
                              const newEndDate = new Date(newStartDate.getTime() + duration);
                              newEnd = newEndDate.toISOString();
                            }
                            
                            setEditedJobData({ 
                              ...editedJobData, 
                              scheduled_start: newStart,
                              scheduled_end: newEnd
                            });
                          }}
                          disabled={editedJobData.flexible_timing}
                          className="mt-1"
                        />
                      ) : (
                        <div className="font-medium">
                          {selectedJobForStatus.flexible_timing ? (
                            <span className="text-orange-600">Time Flexible</span>
                          ) : selectedJobForStatus.scheduled_start ? (
                            new Date(selectedJobForStatus.scheduled_start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                          ) : 'Not set'}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <span className="text-gray-600">End Time:</span>
                      {isEditingJobDetails ? (
                        <Input
                          type="time"
                          value={editedJobData.scheduled_end && editedJobData.scheduled_end.includes('T') ? editedJobData.scheduled_end.split('T')[1]?.substring(0, 5) : ''}
                          onChange={(e) => {
                            const date = editedJobData.scheduled_end ? editedJobData.scheduled_end.split('T')[0] : (editedJobData.scheduled_start ? editedJobData.scheduled_start.split('T')[0] : new Date().toISOString().split('T')[0]);
                            setEditedJobData({ ...editedJobData, scheduled_end: e.target.value ? `${date}T${e.target.value}:00` : '' });
                          }}
                          disabled={editedJobData.flexible_timing}
                          className="mt-1"
                        />
                      ) : (
                        <div className="font-medium">
                          {selectedJobForStatus.scheduled_end ? 
                            new Date(selectedJobForStatus.scheduled_end).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 
                            'N/A'}
                        </div>
                      )}
                    </div>

                    <div className="col-span-2">
                      <span className="text-gray-600">Duration:</span>
                      <div className="font-medium">{getDuration(selectedJobForStatus)} minutes</div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Location Info */}
              <Card className="p-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => !isEditingJobDetails && handleEditJobDetails()}>
                <h3 className="font-semibold mb-3 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Location Details
                  </span>
                  {!isEditingJobDetails && <span className="text-xs text-gray-500">Click to edit</span>}
                </h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <Label>Location Type:</Label>
                    {isEditingJobDetails ? (
                      <Select
                        value={editedJobData.location_type}
                        onValueChange={(value) => setEditedJobData({ ...editedJobData, location_type: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mobile">Mobile (Go to customer)</SelectItem>
                          <SelectItem value="onsite">On-site (Customer comes to us)</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="font-medium capitalize">{selectedJobForStatus.location_type}</div>
                    )}
                  </div>

                  <div>
                    <Label>Location Name:</Label>
                    {isEditingJobDetails ? (
                      <Input
                        value={editedJobData.scheduled_location}
                        onChange={(e) => setEditedJobData({ ...editedJobData, scheduled_location: e.target.value })}
                        placeholder="e.g., Workshop, Customer Site"
                        className="mt-1"
                      />
                    ) : (
                      <div className="font-medium">{selectedJobForStatus.scheduled_location || 'N/A'}</div>
                    )}
                  </div>

                  <div className="border-t pt-3">
                    <Label className="font-semibold">Address:</Label>
                    {isEditingJobDetails ? (
                      <div className="space-y-2 mt-2">
                        <Input
                          value={editedJobData.address_1}
                          onChange={(e) => setEditedJobData({ ...editedJobData, address_1: e.target.value })}
                          placeholder="Address Line 1"
                        />
                        <Input
                          value={editedJobData.address_2}
                          onChange={(e) => setEditedJobData({ ...editedJobData, address_2: e.target.value })}
                          placeholder="Address Line 2"
                        />
                        <Input
                          value={editedJobData.address_3}
                          onChange={(e) => setEditedJobData({ ...editedJobData, address_3: e.target.value })}
                          placeholder="Address Line 3"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={editedJobData.city}
                            onChange={(e) => setEditedJobData({ ...editedJobData, city: e.target.value })}
                            placeholder="City/Town"
                          />
                          <Input
                            value={editedJobData.county}
                            onChange={(e) => setEditedJobData({ ...editedJobData, county: e.target.value })}
                            placeholder="County"
                          />
                        </div>
                        <Input
                          value={editedJobData.postcode}
                          onChange={(e) => setEditedJobData({ ...editedJobData, postcode: e.target.value })}
                          placeholder="Postcode"
                          className="uppercase"
                        />
                      </div>
                    ) : (
                      <div className="font-medium mt-2">
                        {selectedJobForStatus.address_1 || selectedJobForStatus.city || selectedJobForStatus.postcode ? (
                          <div className="space-y-1">
                            {selectedJobForStatus.address_1 && <div>{selectedJobForStatus.address_1}</div>}
                            {selectedJobForStatus.address_2 && <div>{selectedJobForStatus.address_2}</div>}
                            {selectedJobForStatus.address_3 && <div>{selectedJobForStatus.address_3}</div>}
                            {selectedJobForStatus.city && <div>{selectedJobForStatus.city}</div>}
                            {selectedJobForStatus.county && <div>{selectedJobForStatus.county}</div>}
                            {selectedJobForStatus.postcode && <div className="font-semibold">{selectedJobForStatus.postcode}</div>}
                          </div>
                        ) : (
                          'N/A'
                        )}
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-3">
                    <Label>Special Instructions:</Label>
                    {isEditingJobDetails ? (
                      <textarea
                        value={editedJobData.special_instructions}
                        onChange={(e) => setEditedJobData({ ...editedJobData, special_instructions: e.target.value })}
                        placeholder="Any special instructions for this job"
                        className="mt-1 w-full p-2 border rounded-md"
                        rows={3}
                      />
                    ) : (
                      <div className="font-medium mt-1">{selectedJobForStatus.special_instructions || 'N/A'}</div>
                    )}
                  </div>
                </div>
              </Card>

              {/* Job Items */}
              {selectedJobForStatus.items && selectedJobForStatus.items.length > 0 && (
                <Card className="p-4">
                  <h3 className="font-semibold mb-3">Services / Items</h3>
                  <div className="space-y-2">
                    {selectedJobForStatus.items.map((item, index) => {
                      const service = services.find(s => s.id === item.service_product_id);
                      const itemVehicle = item.vehicle_id ? vehicles.find(v => v.id === item.vehicle_id) : null;
                      const VehicleIcon = itemVehicle ? getVehicleIcon(itemVehicle.vehicle_type) : Car;
                      
                      return (
                        <div key={index} className="p-3 bg-gray-50 rounded border-l-4 border-blue-400">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-medium text-sm mb-1">
                                {service?.name || item.description || 'Service'}
                              </div>
                              
                              {/* Vehicle Info */}
                              {itemVehicle ? (
                                <div className="flex items-center gap-1.5 text-xs text-blue-700 mb-1">
                                  <VehicleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span className="font-bold">{itemVehicle.registration_number}</span>
                                  <span className="text-gray-600">
                                    {itemVehicle.make} {itemVehicle.model}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5 text-xs text-blue-700 mb-1">
                                  <Car className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span className="font-bold">TBA</span>
                                </div>
                              )}
                              
                              <div className="text-xs text-gray-600">
                                Qty: {item.quantity} × £{item.unit_price?.toFixed(2)}
                              </div>
                            </div>
                            <div className="font-semibold text-lg ml-4">
                              £{item.line_total?.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}

              {/* Financial Summary */}
              <Card className="p-4 bg-green-50">
                <h3 className="font-semibold mb-3">Financial Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">£{selectedJobForStatus.subtotal?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax:</span>
                    <span className="font-medium">£{selectedJobForStatus.tax?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total:</span>
                    <span>£{selectedJobForStatus.total?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              </Card>

              {/* Notes */}
              <Card className="p-4">
                <h3 className="font-semibold mb-2">Notes</h3>
                {isEditingJobDetails ? (
                  <textarea
                    value={editedJobData.notes}
                    onChange={(e) => setEditedJobData({ ...editedJobData, notes: e.target.value })}
                    placeholder="Internal notes about this job"
                    className="w-full p-2 border rounded-md"
                    rows={4}
                  />
                ) : (
                  <p className="text-sm text-gray-700">{selectedJobForStatus.notes || 'No notes'}</p>
                )}
              </Card>

              {/* Actions */}
              <div className="flex gap-2 justify-end">
                {isEditingJobDetails ? (
                  <>
                    <Button variant="outline" onClick={handleCancelEditJobDetails}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveJobDetails}>
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={handleEditJobDetails}>
                      Edit Details
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsJobDetailsDialogOpen(false);
                        openStatusDialog(selectedJobForStatus);
                      }}
                    >
                      Change Status
                    </Button>
                    {selectedJobForStatus.status === 'unscheduled' && (
                      <Button
                        onClick={() => {
                          setIsJobDetailsDialogOpen(false);
                          setSelectedJobForScheduling(selectedJobForStatus);
                          setIsScheduleDialogOpen(true);
                        }}
                      >
                        Schedule Job
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => {
                        // Generate print HTML for the selected job
                        const printContent = generatePrintHTML(selectedJobForStatus);
                        
                        // Open new window and print
                        const printWindow = window.open('', '_blank');
                        printWindow.document.write(printContent);
                        printWindow.document.close();
                        
                        // Wait for content to load, then print
                        printWindow.onload = () => {
                          printWindow.print();
                          printWindow.onafterprint = () => {
                            printWindow.close();
                          };
                        };
                      }}
                      className="gap-2"
                    >
                      <Printer className="w-4 h-4" />
                      Print
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsJobDetailsDialogOpen(false)}
                    >
                      Close
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Scheduling Dialog */}
      <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule Job</DialogTitle>
            <DialogDescription>
              Set date, time, and location for Job {selectedJobForScheduling?.job_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg mb-4">
              <input
                type="checkbox"
                id="flexible_timing"
                checked={scheduleData.flexible_timing}
                onChange={(e) => setScheduleData({ 
                  ...scheduleData, 
                  flexible_timing: e.target.checked
                })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <Label htmlFor="flexible_timing" className="cursor-pointer font-normal">
                Customer is flexible on time (Time Not Set - date still required)
              </Label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="scheduled_date">Date *</Label>
                <Input
                  id="scheduled_date"
                  type="date"
                  value={scheduleData.scheduled_start ? scheduleData.scheduled_start.split('T')[0] : ''}
                  onChange={(e) => {
                    const time = scheduleData.scheduled_start?.split('T')[1] || '09:00';
                    setScheduleData({ ...scheduleData, scheduled_start: `${e.target.value}T${time}` });
                  }}
                  required
                />
              </div>
              <div>
                <Label htmlFor="scheduled_time">Start Time {!scheduleData.flexible_timing && '*'}</Label>
                <Input
                  id="scheduled_time"
                  type="time"
                  value={scheduleData.scheduled_start ? scheduleData.scheduled_start.split('T')[1]?.substring(0, 5) : ''}
                  onChange={(e) => {
                    const date = scheduleData.scheduled_start?.split('T')[0] || '';
                    setScheduleData({ ...scheduleData, scheduled_start: `${date}T${e.target.value}` });
                  }}
                  disabled={scheduleData.flexible_timing}
                  required={!scheduleData.flexible_timing}
                  placeholder={scheduleData.flexible_timing ? "Flexible" : ""}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="scheduled_end_date">End Date</Label>
                <Input
                  id="scheduled_end_date"
                  type="date"
                  value={scheduleData.scheduled_end ? scheduleData.scheduled_end.split('T')[0] : ''}
                  onChange={(e) => {
                    const time = scheduleData.scheduled_end?.split('T')[1] || '17:00';
                    setScheduleData({ ...scheduleData, scheduled_end: `${e.target.value}T${time}` });
                  }}
                />
              </div>
              <div>
                <Label htmlFor="scheduled_end_time">End Time</Label>
                <Input
                  id="scheduled_end_time"
                  type="time"
                  value={scheduleData.scheduled_end ? scheduleData.scheduled_end.split('T')[1]?.substring(0, 5) : ''}
                  onChange={(e) => {
                    const date = scheduleData.scheduled_end?.split('T')[0] || '';
                    setScheduleData({ ...scheduleData, scheduled_end: `${date}T${e.target.value}` });
                  }}
                  disabled={scheduleData.flexible_timing}
                  placeholder={scheduleData.flexible_timing ? "Flexible" : ""}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="location_type">Location Type</Label>
              <Select 
                value={scheduleData.location_type} 
                onValueChange={(value) => {
                  setScheduleData({ ...scheduleData, location_type: value });
                  // Auto-populate customer address if mobile
                  if (value === 'mobile' && selectedJobForScheduling) {
                    const customer = customers.find(c => c.id === selectedJobForScheduling.customer_id);
                    if (customer) {
                      setScheduleData({
                        ...scheduleData,
                        location_type: value,
                        address_1: customer.address_1 || '',
                        address_2: customer.address_2 || '',
                        address_3: customer.address_3 || '',
                        city: customer.city || '',
                        county: customer.county || '',
                        postcode: customer.postcode || ''
                      });
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mobile">Mobile (Go to customer)</SelectItem>
                  <SelectItem value="onsite">On-site (Customer comes to us)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="scheduled_location">Location Name</Label>
              <Input
                id="scheduled_location"
                value={scheduleData.scheduled_location}
                onChange={(e) => setScheduleData({ ...scheduleData, scheduled_location: e.target.value })}
                placeholder="e.g., Customer's Home, Main Workshop"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Address</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setScheduleData({
                    ...scheduleData,
                    address_1: '',
                    address_2: '',
                    address_3: '',
                    city: '',
                    county: '',
                    postcode: ''
                  })}
                  className="text-xs h-7"
                >
                  Clear Address
                </Button>
              </div>
              <Input
                value={scheduleData.address_1}
                onChange={(e) => setScheduleData({ ...scheduleData, address_1: e.target.value })}
                placeholder="Address Line 1"
              />
              <Input
                value={scheduleData.address_2}
                onChange={(e) => setScheduleData({ ...scheduleData, address_2: e.target.value })}
                placeholder="Address Line 2"
              />
              <Input
                value={scheduleData.address_3}
                onChange={(e) => setScheduleData({ ...scheduleData, address_3: e.target.value })}
                placeholder="Address Line 3"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={scheduleData.city}
                  onChange={(e) => setScheduleData({ ...scheduleData, city: e.target.value })}
                  placeholder="City/Town"
                />
                <Input
                  value={scheduleData.county}
                  onChange={(e) => setScheduleData({ ...scheduleData, county: e.target.value })}
                  placeholder="County"
                />
              </div>
              <Input
                value={scheduleData.postcode}
                onChange={(e) => setScheduleData({ ...scheduleData, postcode: e.target.value.toUpperCase() })}
                placeholder="Postcode"
                className="uppercase"
              />
            </div>

            {/* Driver Assignment */}
            {drivers.length > 0 && (
              <div>
                <Label htmlFor="assigned_driver">Assign Driver (Optional)</Label>
                <select
                  id="assigned_driver"
                  value={selectedJobForScheduling?.assigned_driver_id || ''}
                  onChange={async (e) => {
                    const driverId = e.target.value || null;
                    // Update job immediately when driver is assigned
                    try {
                      await axios.patch(
                        `${API}/jobs/${selectedJobForScheduling.id}`,
                        { assigned_driver_id: driverId },
                        getAuthHeaders()
                      );
                      setSelectedJobForScheduling({
                        ...selectedJobForScheduling,
                        assigned_driver_id: driverId
                      });
                      toast.success('Driver assigned successfully');
                    } catch (error) {
                      toast.error('Failed to assign driver');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No driver assigned</option>
                  {drivers.filter(d => d.is_active).map(driver => (
                    <option key={driver.id} value={driver.id}>
                      {driver.first_name} {driver.last_name} ({driver.role})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <Label htmlFor="special_instructions">Special Instructions</Label>
              <textarea
                id="special_instructions"
                value={scheduleData.special_instructions}
                onChange={(e) => setScheduleData({ ...scheduleData, special_instructions: e.target.value })}
                className="w-full px-3 py-2 border rounded-md text-sm"
                rows="2"
                placeholder="Any special notes or requirements..."
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsScheduleDialogOpen(false);
                  setSelectedJobForScheduling(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button onClick={handleScheduleJob} className="flex-1">
                Schedule Job
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Job Dialog - Screen View */}
      <Dialog open={isPrintDialogOpen} onOpenChange={(open) => {
        setIsPrintDialogOpen(open);
        if (!open) {
          setJobToPrint(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Print Job Details - {jobToPrint?.job_number}</DialogTitle>
          </DialogHeader>
          
          {jobToPrint && (
            <div className="space-y-4">
              {/* Show preview of what will print */}
              <div className="bg-gray-50 p-4 rounded border">
                <p className="text-sm text-gray-600 mb-2">Preview: This job will be printed with all details including customer info, vehicles, schedule, location, services, and financial summary.</p>
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsPrintDialogOpen(false);
                    setJobToPrint(null);
                  }}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    // Generate print HTML
                    const printContent = generatePrintHTML(jobToPrint);
                    
                    // Open new window and print
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(printContent);
                    printWindow.document.close();
                    
                    // Wait for content to load, then print
                    printWindow.onload = () => {
                      printWindow.print();
                      printWindow.onafterprint = () => {
                        printWindow.close();
                      };
                    };
                  }}
                  className="gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Status Dialog */}
      <ChangeStatusDialog
        open={changeStatusDialogOpen}
        onOpenChange={setChangeStatusDialogOpen}
        currentStatus={jobToChangeStatus?.status}
        availableStatuses={jobStatuses}
        onConfirm={handleChangeStatus}
        itemType="job"
      />
    </div>
  );
};

export default JobsManagement;
